import moment from 'moment-timezone'
import { map } from 'ramda'

import { Report } from '@driftt/api-types'

import { IQueryConfig } from '../query/types'
import {
  IConversationLiveChatRating,
  IConversationRep,
  IConversationRepPerformance,
  IConversationTag,
  IConversationURL,
  LineGraphResponse,
} from '../types'

const ensureNoNan = (value): number => (value === 'NaN' ? 0 : value)

export const parseLineChart = (
  response: Report<[string]>
): LineGraphResponse => {
  return map(
    (row) => [row.dimensions[0], ensureNoNan(row.metrics[0])],
    response.data.rows
  )
}

/**
 * Normalize the heatmap values by dividing by the number of "buckets" for each data point
 */
export const parseHeatmap = (
  response: Report<[number, number]>,
  queryConfig: IQueryConfig
) => {
  const timezone = queryConfig.timezone!

  return map((row) => {
    const day = (row.dimensions[0] as number) % 7
    const hour = row.dimensions[1]
    const value = ensureNoNan(row.metrics[0])
    const start = moment(queryConfig.startDate).tz(timezone)

    // If the end date of the range is after "now", make "now" the end of our range instead.
    // This prevents us from under counting the current day which is only partially complete.
    const now = moment().tz(timezone)
    let end = moment(queryConfig.endDate).tz(timezone).add(1, 'day')
    if (end.isAfter(now)) {
      end = now
    }

    // Compute the number of times this [day, hour] pair appears within the selected date range.
    // We can compute this by finding the first occurrence of each day-of-week after the start date,
    // and subtracting from the end date to find the number total number of hours between them.
    // Lastly we divide by the number of hours in a week to find the correct number of buckets.
    //
    // See: https://momentjs.com/docs/#/get-set/day/
    const firstOccurrenceOfDayOfWeek = start
      .day(day < start.day() ? day + 7 : day)
      .add(hour, 'h')
    const buckets = Math.ceil(
      end.diff(firstOccurrenceOfDayOfWeek, 'hours') / (7 * 24)
    )

    if (buckets > 0) {
      return [day, hour, Math.ceil(value / buckets)]
    } else {
      return [day, hour, 0]
    }
  }, response.data.rows)
}

export const parseUrlTable = (
  response: Report<[string]>
): IConversationURL[] => {
  return map(
    (row) => ({
      url: row.dimensions[0],
      value: ensureNoNan(row.metrics[0]),
    }),
    response.data.rows
  )
}

export const parseTopTagsTable = (
  response: Report<[string]>
): IConversationTag[] => {
  return response.data.rows.map((row) => ({
    tag: row.dimensions[0],
    value: ensureNoNan(row.metrics[0]),
    percentage: ((ensureNoNan(row.metrics[0]) / response.count!) * 100).toFixed(
      0
    ),
    totalCount: response.count!,
  }))
}

export const parseRepTable = (response: Report): IConversationRep[] => {
  return map(
    (row) => ({
      user: row.dimensions[0] as number,
      value: ensureNoNan(row.metrics[0]),
    }),
    response.data.rows
  )
}

export const parseRoutingTable = (response: Report) => {
  return map(
    (row) => ({
      type: row.dimensions[0],
      value: ensureNoNan(row.metrics[0]),
    }),
    response.data.rows
  )
}

export const parseRoutingTableRules = (response: Report) => {
  return map(
    (row) => ({
      type: row.dimensions[0],
      routingRule: true,
      value: ensureNoNan(row.metrics[0]),
    }),
    response.data.rows
  )
}

export const parseRoutingTablePlaybooks = (response: Report) => {
  return map(
    (row) => ({
      type: row.dimensions[0],
      playbookId: row.dimensions[0],
      value: ensureNoNan(row.metrics[0]),
    }),
    response.data.rows
  )
}

export const parseRoutingTablePlaybookActions = (response: Report) => {
  return map(
    (row) => ({
      type: row.dimensions[0],
      playbookId: row.dimensions[0],
      isAction: true,
      value: ensureNoNan(row.metrics[0]),
    }),
    response.data.rows
  )
}

export const parseRoutingTablePlaybookGoals = (
  response: Report<[number, number]>
) => {
  return map(
    (row) => ({
      type: row.dimensions[1],
      goalId: row.dimensions[1],
      playbookId: row.dimensions[0],
      value: ensureNoNan(row.metrics[0]),
    }),
    response.data.rows
  )
}

export const parseRepPerformanceTable = (
  response: Report
): IConversationRepPerformance[] => {
  return map(
    (row) => ({
      user: row.dimensions[0] as number,
      newConvos: ensureNoNan(row.metrics[0]),
      responseTime: ensureNoNan(row.metrics[1]),
      convoLength: ensureNoNan(row.metrics[2]),
      avgResponseTime: ensureNoNan(row.metrics[3]),
    }),
    response.data.rows
  )
}

export const parseConversationRatingsByRep = (
  response: Report
): IConversationLiveChatRating[] => {
  return map((row) => {
    const agentId = row.dimensions[0] as number
    const liveChatRating = row.metrics[1].toFixed(1)
    return {
      agentId,
      liveChatRating,
    }
  }, response.data.rows)
}

export const parseMedian = (response: Report) => {
  return { time: ensureNoNan(response.data.rows[0].metrics[0]) }
}
